import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { FileText } from "lucide-react";
import { useExpertApplicationActions } from "@/hooks/useExpertApplicationActions";
import { usePublishedExperts } from "@/hooks/usePublishedExperts";
import { useExpertStatus } from "@/hooks/useExpertStatus";
import ExpertProfileEditForm from "./ExpertProfileEditForm";
import WorkflowProgressIndicator from "./WorkflowProgressIndicator";
import CurrentStatusDisplay from "./CurrentStatusDisplay";
import PublishedExpertActions from "./PublishedExpertActions";
import PendingApplicationActions from "./PendingApplicationActions";
import RejectedApplicationActions from "./RejectedApplicationActions";
import ApprovedNotPublishedStatus from "./ApprovedNotPublishedStatus";
import EnhancedPublishingSection from "./EnhancedPublishingSection";
import BookingLinkManager from "./BookingLinkManager";
import { ExpertApplication } from "@/types/admin";

interface WorkflowControlsProps {
  application: ExpertApplication;
  onStatusUpdate: (applicationId: string, status: 'approved' | 'rejected') => void;
  loading: boolean;
}

export default function WorkflowControls({ 
  application, 
  onStatusUpdate, 
  loading 
}: WorkflowControlsProps) {
  const { publishedExperts, fetchPublishedExperts } = usePublishedExperts();
  const { handleUnpublishExpert } = useExpertApplicationActions(() => {
    fetchPublishedExperts();
  });
  const [isEditing, setIsEditing] = useState(false);

  // Use the enhanced expert status hook
  const {
    expertStatus,
    loading: statusLoading,
    handleToggleExpertStatus,
    refreshStatus
  } = useExpertStatus(application.id, application.status === 'approved');

  // Check if this application has a published expert profile
  const publishedExpert = publishedExperts.find(expert => 
    expert.application_id === application.id
  );

  const canApprove = application.status === 'pending';
  const isPublished = expertStatus === 'active';

  const handleEditSave = () => {
    fetchPublishedExperts();
    refreshStatus();
  };

  if (isEditing && publishedExpert) {
    return (
      <ExpertProfileEditForm
        expertId={publishedExpert.id}
        expertData={publishedExpert}
        onClose={() => setIsEditing(false)}
        onSave={handleEditSave}
      />
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Application Workflow
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Progress Indicator */}
          <WorkflowProgressIndicator 
            status={application.status} 
            isPublished={isPublished} 
          />

          {/* Current Status */}
          <CurrentStatusDisplay 
            status={application.status} 
            isPublished={isPublished} 
          />

          {/* Published Expert Actions */}
          {publishedExpert && (
            <PublishedExpertActions
              onEdit={() => setIsEditing(true)}
              onUnpublish={() => handleUnpublishExpert(application.id)}
              loading={loading}
            />
          )}

          {/* Pending Application Actions */}
          {canApprove && !publishedExpert && (
            <PendingApplicationActions
              applicationId={application.id}
              onStatusUpdate={onStatusUpdate}
              loading={loading}
            />
          )}

          {/* Rejected Application Actions */}
          {application.status === 'rejected' && (
            <RejectedApplicationActions
              application={application}
              onStatusUpdate={onStatusUpdate}
              loading={loading}
            />
          )}

          {/* Approved but not Published */}
          {application.status === 'approved' && !isPublished && (
            <ApprovedNotPublishedStatus />
          )}
        </CardContent>
      </Card>

      {/* Booking Link Manager - Only show for approved applications */}
      {application.status === 'approved' && (
        <BookingLinkManager
          applicationId={application.id}
          isApproved={application.status === 'approved'}
          onBookingLinkUpdated={() => {
            fetchPublishedExperts();
            refreshStatus();
          }}
        />
      )}

      {/* Enhanced Publishing Section - Only show for approved applications */}
      {application.status === 'approved' && (
        <EnhancedPublishingSection
          applicationId={application.id}
          isApproved={application.status === 'approved'}
          expertStatus={expertStatus}
          loading={statusLoading}
          onToggleStatus={handleToggleExpertStatus}
        />
      )}
    </div>
  );
}
